import React, { useState } from "react";
import { CrossIcon } from "../../common/icons";
import type { EnglishCourseDataWithId } from "../../content/english-course-page";
import { useStore } from "@nanostores/react";
import $auth from "../../store/user";
import {
  sendCourseSuggestionEmail,
  sendShortCourseSuggestionEmail,
  sendScholarshipSuggestionEmail,
} from "../../utils/api"; // Adjust import path as needed
import type { ScholarshipRecommendation } from "../../content/course-detail-page";

export interface Course {
  course_slug?: string;
  short_course_slug?: string;
  id: number;
  name: string;
  duration?: string;
  institute_name: string;
  logo: string;
  fee: number | null;
  duration_id?: number;
  course_fee_id?: number;
  short_course_fee_id?: number;
  course_scale_id?: number;
  type?: string;
  institute_slug?: string;
}

export interface SaveCourse {
  id: number;
  name: string;
  slug: string;
  course_fee_id?: number;
  fee: number | null;
  duration_id?: number;
  campus: {
    id: number;
    campus: string;
    latitude: number;
    longitude: number;
    slug: string;
    institute: {
      id: number;
      name: string;
      logo: string;
      sector: string;
      institute_type: string;
      slug: string;
    };
  };
}

export interface University {
  id: number;
  institute_name: string;
  sector?: string;
  logo: string;
  slug?: string;
}

export interface Institute {
  id: number;
  name: string;
  logo: string;
}

export interface Scholarship {
  id: number;
  scholarship_name: string;
  institute: Institute;
  slug: string;
}

export type EntityType =
  | "course"
  | "university"
  | "scholarship"
  | "savecourse"
  | "short-course";
export type Entity =
  | Course
  | University
  | Scholarship
  | SaveCourse
  | EnglishCourseDataWithId;

// Define CourseRecommendation interface for API request
interface CourseRecommendation {
  type: string;
  name: string;
  fee: number | null;
  institute: string;
  duration: string | null;

  url: string;
}

// Define ShortCourseRecommendation interface for API request
interface ShortCourseRecommendation {
  type: string;
  name: string;
  university: string;
  duration: string | null;
  fee: number | null;
  url: string;
}

interface CompareModalProps<T extends Entity> {
  open: boolean;
  onClose: () => void;
  selectedEntities: T[];
  entityType: EntityType;
  setSelectedEntities: React.Dispatch<React.SetStateAction<T[]>>;
  defaultImage?: string;
}

export function CompareModal<T extends Entity>({
  open,
  onClose,
  selectedEntities,
  defaultImage,
  entityType,
  setSelectedEntities,
}: CompareModalProps<T>) {
  if (!open) return null;
  const auth = useStore($auth);
  console.log("selectedEntities", selectedEntities);
  // State for student information
  const [studentName, setStudentName] = useState("");
  const [studentEmail, setStudentEmail] = useState("");
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleRemoveEntity = (entityId: number) => {
    setSelectedEntities((entities) =>
      entities.filter((entity) => entity.id !== entityId),
    );
  };
  console.log("Selected entities:", selectedEntities);
  const handleClear = () => {
    setSelectedEntities([]);
    setShowStudentForm(false);
    setStudentName("");
    setStudentEmail("");
    setSubmitSuccess(false);
    setSubmitError("");
    onClose();
  };

  const constructRecommendationUrl = (entity: T) => {
    const customDomain = "https://timescoursefinder.com";
    const compareRoutes: Record<EntityType, string> = {
      course: "course",
      university: "university-detail",
      scholarship: "scholarships",
      savecourse: "course",
      "short-course": "short-course",
    };
    const route = compareRoutes[entityType];

    switch (entityType) {
      case "course": {
        const course = entity as Course;
        return `${customDomain}/${course.institute_slug}/${course.course_slug}/${course.duration_id}/${course.course_fee_id}`;
      }
      case "short-course": {
        const course = entity as Course;
        return `${customDomain}/${route}/${course.short_course_slug}/${course.duration_id}/${course.short_course_fee_id}/${course.course_scale_id}`;
      }
      case "savecourse": {
        const course = entity as SaveCourse;
        return `${customDomain}/${route}/${course.slug}/${course.course_fee_id}/${course.duration_id}`;
      }
      case "university": {
        const university = entity as University;
        return `${customDomain}/${route}/${university.slug}`;
      }
      case "scholarship": {
        const scholarship = entity as Scholarship;
        return `${customDomain}/${route}/${scholarship.slug}`;
      }
      default:
        return "";
    }
  };
  const creatScholarshipRecommendationsArray =
    (): ScholarshipRecommendation[] => {
      return selectedEntities.map((entity) => {
        let name = "";
        let university = "";

        if (entityType === "scholarship") {
          const scholarship = entity as Scholarship;
          name = scholarship.scholarship_name;
          university = scholarship.institute.name;
        }

        return {
          type: "scholarship",
          name,
          university,

          url: constructRecommendationUrl(entity),
        };
      });
    };

  const createShortCourseRecommendationsArray =
    (): ShortCourseRecommendation[] => {
      return selectedEntities.map((entity) => {
        let name = "";
        let university = "";
        let duration: string | null = null;
        let fee: number | null = null;

        if (entityType === "short-course") {
          const course = entity as Course;
          name = course.name;
          university = course.institute_name;
          duration = course.duration ? `${course.duration}` : null;
          fee = course.fee ? course.fee : null;
        }

        return {
          type: "short_course", // API expects "short_course" instead of "short-course"
          name,
          university,
          duration,
          fee,
          url: constructRecommendationUrl(entity),
        };
      });
    };

  const createRecommendationsArray = (): CourseRecommendation[] => {
    return selectedEntities.map((entity) => {
      let name = "";
      let institute = "";
      let duration: string | null = null;
      let fee: number | null = null;

      switch (entityType) {
        case "course": {
          const course = entity as Course;

          name = course.name;
          institute = course.institute_name;
          duration = course.duration ? `${course.duration}` : null;
          fee = course.fee ? course.fee : null;
          break;
        }
        case "university": {
          const university = entity as University;
          name = university.institute_name;
          institute = university.institute_name;
          duration = "N/A";
          break;
        }
        case "scholarship": {
          const scholarship = entity as Scholarship;
          name = scholarship.scholarship_name;
          institute = scholarship.institute.name;
          duration = "N/A";
          break;
        }
        case "savecourse": {
          const savecourse = entity as SaveCourse;
          name = savecourse.name;
          institute = savecourse.campus.institute.name;
          duration = savecourse.duration_id
            ? `${savecourse.duration_id} Years`
            : "2 Years";
          fee = savecourse.fee ? savecourse.fee : null;

          break;
        }
      }

      return {
        type: entityType,
        name,
        institute,
        duration,
        fee,
        url: constructRecommendationUrl(entity),
      };
    });
  };

  const handleSendEmail = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!studentName || !studentEmail) {
      setSubmitError("Please enter student name and email");
      return;
    }

    if (!auth?.user?.email) {
      setSubmitError("Consultant email not found. Please log in again.");
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      if (entityType === "short-course") {
        const recommendations = createShortCourseRecommendationsArray();

        await sendShortCourseSuggestionEmail(
          studentName,
          studentEmail,
          auth.user.email,
          recommendations,
        );
      } else if (entityType === "scholarship") {
        const recommendations = creatScholarshipRecommendationsArray();
        await sendScholarshipSuggestionEmail(
          studentName,
          studentEmail,
          auth.user.email,
          recommendations,
        );
      } else {
        const recommendations = createRecommendationsArray();

        await sendCourseSuggestionEmail(
          studentName,
          studentEmail,
          auth.user.email,
          recommendations,
        );
      }

      setSubmitSuccess(true);
      setTimeout(() => {
        handleClear();
      }, 2000);
    } catch (error) {
      console.error("Failed to send email:", error);
      setSubmitError("Failed to send email. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClickBack = () => {
    setShowStudentForm(false);
    setStudentName("");
    setStudentEmail("");
  };
  const handleCompare = () => {
    if (selectedEntities.length < 1) {
      return;
    }

    // Show student form instead of opening email
    setShowStudentForm(true);
  };

  const renderEntityCard = (entity: T) => {
    let entityName = "";
    let entityLogo = "";
    let entitySubtitle = "";

    switch (entityType) {
      case "course": {
        const course = entity as Course;
        entityName = course.name;
        entityLogo = course.logo;
        entitySubtitle = course.institute_name;
        break;
      }
      case "short-course": {
        const course = entity as Course;
        entityName = course.name;
        entityLogo = course.logo;
        entitySubtitle = course.institute_name;
        break;
      }
      case "university": {
        const university = entity as University;
        entityName = university.institute_name;
        entityLogo = university.logo;
        entitySubtitle = university.sector || "";
        break;
      }
      case "scholarship": {
        const scholarship = entity as Scholarship;
        entityName = scholarship.scholarship_name;
        entityLogo = scholarship.institute.logo;
        entitySubtitle = scholarship.institute.name;
        break;
      }
      case "savecourse": {
        const savecourse = entity as SaveCourse;
        entityName = savecourse.name;
        entityLogo = savecourse.campus.institute.logo;
        entitySubtitle = savecourse.campus.institute.name;
        break;
      }
    }

    return (
      <div
        key={entity.id}
        className="flex items-center relative border p-2 rounded-2xl shadow-sm min-w-[220px] max-w-[250px] bg-white flex-shrink-0"
      >
        <button
          onClick={() => handleRemoveEntity(entity.id)}
          className="absolute top-1 right-1"
        >
          <CrossIcon className="w-4 h-4" />
        </button>

        <div className="bg-primary-50 p-2 rounded-xl flex-shrink-0">
          <img
            src={entityLogo || defaultImage}
            alt={entityName}
            className="w-14 h-14 object-cover rounded"
          />
        </div>

        <div className="ml-3">
          <p className="leading-4 font-medium text-sm text-title">
            {entityName}
          </p>
          {entitySubtitle && (
            <p className="text-xs text-gray-500 mt-1">{entitySubtitle}</p>
          )}
        </div>
      </div>
    );
  };

  const maxEntities = 3;
  const displayEntityType =
    entityType === "short-course" ? "short course" : entityType;

  // Render student form when needed
  if (showStudentForm) {
    return (
      <div className="z-[60] fixed bottom-0 left-0 right-0 bg-white shadow-lg rounded-t-xl p-4 flex flex-col items-center justify-between">
        <div className="mx-auto md:max-w-md w-full flex flex-col gap-4 p-4">
          <h3 className="text-lg font-semibold text-center">
            Student Information
          </h3>

          {submitSuccess && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
              Recommendations sent successfully!
            </div>
          )}

          {submitError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSendEmail} className="space-y-4">
            <div>
              <label
                htmlFor="studentName"
                className="block text-sm font-medium text-gray-700"
              >
                Student Name
              </label>
              <input
                type="text"
                id="studentName"
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                placeholder="Enter student name"
                required
              />
            </div>

            <div>
              <label
                htmlFor="studentEmail"
                className="block text-sm font-medium text-gray-700"
              >
                Student Email
              </label>
              <input
                type="email"
                id="studentEmail"
                value={studentEmail}
                onChange={(e) => setStudentEmail(e.target.value)}
                className="mt-1 p-2 w-full border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                placeholder="Enter student email"
                required
              />
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex-1 px-4 py-2 text-sm rounded-full transition-all font-normal text-center ${
                  isSubmitting
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-primary text-white hover:bg-primary-800"
                }`}
              >
                {isSubmitting ? "Sending..." : "Send Recommendations"}
              </button>

              <button
                type="button"
                onClick={handleClickBack}
                className="flex-1 px-4 py-2 text-sm border border-border-100 rounded-full transition-all bg-white text-title font-normal text-center"
              >
                Back
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="z-[60] fixed bottom-0 left-0 right-0 bg-white shadow-lg rounded-t-xl p-4 flex flex-col md:flex-row items-center justify-between overflow-x-auto">
      <div className="mx-auto md:max-w-6xl w-full flex flex-col md:flex-row gap-4">
        {/* Selected entities container */}
        <div className="flex-1">
          <div className="flex space-x-3 overflow-x-auto pb-2">
            {selectedEntities.map((entity) => renderEntityCard(entity))}

            {selectedEntities.length < maxEntities && (
              <div className="w-[200px] h-[90px] flex items-center justify-center border border-dashed p-2 rounded-2xl text-gray-400">
                <p className="text-xs text-center">
                  {selectedEntities.length === 0
                    ? `Select up to ${maxEntities} ${displayEntityType}s`
                    : `Add ${maxEntities - selectedEntities.length} more ${displayEntityType}${
                        maxEntities - selectedEntities.length > 1 ? "s" : ""
                      }`}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Buttons Section */}
        <div className="flex md:flex-col items-center space-y-0 space-x-3 md:space-y-3 md:space-x-0 flex-wrap justify-center gap-3 md:gap-0">
          <button
            onClick={handleCompare}
            disabled={selectedEntities.length < 1}
            className={`w-[120px] px-4 py-2 text-sm rounded-full transition-all font-normal text-center ${
              selectedEntities.length < 1
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-primary text-white hover:bg-primary-800"
            }`}
          >
            Send
          </button>
          <button
            onClick={handleClear}
            className="w-[120px] px-4 py-2 text-sm border border-border-100 rounded-full transition-all bg-white text-title font-normal text-center"
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}

export default CompareModal;
